import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(() => {
    // Load user data and expiration timestamp from localStorage
    const storedUserData = localStorage.getItem("userData");
    if (storedUserData) {
      const { userData, expires } = JSON.parse(storedUserData);

      // Check if the data has expired
      if (expires && expires < Date.now()) {
        // If expired, reset the data
        localStorage.removeItem("userData");
        return null;
      }

      return userData;
    }

    return {};
  });

  const [keepSignedIn, setKeepSignedIn] = useState(false);

  useEffect(() => {
    // Save user data and expiration timestamp to localStorage whenever it changes
    if (keepSignedIn) {
      localStorage.setItem(
        "userData",
        JSON.stringify({
          userData,
          // Set the expiration time.
          expires: Date.now() + 2 * 60 * 60 * 1000, // 2 * 60 * 60 * 1000 === 2Hrs
        })
      );
    }
  }, [userData, keepSignedIn]);

  const [scrollHeight, setScrollHeight] = useState(0);

  useEffect(() => {
    const updateScrollHeight = () => {
      setScrollHeight(document.body.scrollHeight);
    };

    updateScrollHeight();

    const handleScroll = () => {
      updateScrollHeight();
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const fetchDashboardData = async (showDialogCallback) => {
    try {
      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/active_tokens_sessions",
        {
          email: userData.email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.token}`,
          },
        }
      );

      const data = response.data;
      let subscriptionPlan, subscriptionFrequency;

      switch (data?.data[0]?.subscription_type) {
        case "student":
          subscriptionPlan = "Student";
          break;
        case "pro":
          subscriptionPlan = "Professional";
          break;
        case "business":
          subscriptionPlan = "Business";
          break;
        case "enterprise":
          subscriptionPlan = "Enterprise";
          break;
        default:
          subscriptionPlan = "Free Trial";
          break;
      }

      switch (data?.data[0]?.subscription_frequency) {
        case "yearly":
          subscriptionFrequency = "Yearly";
          break;
        case "monthly":
          subscriptionFrequency = "Monthly";
          break;
        default:
          subscriptionFrequency = "One-Time";
          break;
      }

      setUserData((prevUserData) => ({
        ...prevUserData,
        session_stats: {
          remaining: data?.data[0]?.sessions_remaining,
          total: data?.data[0]?.total_sessions,
        },
        token_stats: {
          add_on_tokens_remaining: data?.data[0]?.add_on_tokens_remaining,
          add_on_tokens_total: data?.data[0]?.add_on_tokens_total,
          daily_subscription_tokens_remaining:
            data?.data[0]?.daily_subscription_tokens_remaining,
          daily_subscription_tokens_total:
            data?.data[0]?.daily_subscription_tokens_total,
          subscription_tokens_remaining:
            data?.data[0]?.subscription_tokens_remaining,
          subscription_total_tokens: data?.data[0]?.subscription_total_tokens,
          remaining: data?.data[0]?.tokens_remaining,
          total: data?.data[0]?.total_tokens,
        },
        subscription_plan: subscriptionPlan,
        subscription_frequency: subscriptionFrequency,
        valid_through: data?.data[0]?.subscription_validity,
      }));
    } catch (error) {
      console.error("Error fetching data:", error);

      if (error.response && error.response.status === 400) {
        showDialogCallback();
        setUserData((prevUserData) => ({
          ...prevUserData,
          session_stats: {
            remaining: 0,
            total: 0,
          },
          token_stats: {
            remaining: 0,
            total: 0,
          },
          subscription_plan: "Plan Expired",
          subscription_frequency: "NA",
          valid_through: "NA",
        }));
      }
    }
  };

  const [isContentLoading, setIsContentLoading] = useState(false);
  const [isSuggestedBoxOpen, setIsSuggestedBoxOpen] = useState(false);

  // Function to handle generating sample questions
  const handleGenerateSampleQuestions = async (activeSessionId) => {
    try {
      setIsContentLoading(true);

      const response = await axios.post(
        "https://soliboat.solidaridadasia.com/generate_sample_questions",
        {
          email: userData.email,
          session_id: activeSessionId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
        }
      );

      setUserData((prevUserData) => {
        const updatedSessionData = prevUserData.session_data.map((session) => {
          if (session.session_id === activeSessionId) {
            // Update sample_questions for the specific session
            return {
              ...session,
              sample_questions: response.data?.data[0]?.answer,
            };
          }
          return session; // Return unchanged if not the targeted session
        });

        return {
          ...prevUserData,
          session_data: updatedSessionData,
        };
      });
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setIsContentLoading(false);
      setIsSuggestedBoxOpen(true);
    }
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        keepSignedIn,
        setKeepSignedIn,
        fetchDashboardData,
        scrollHeight,
        handleGenerateSampleQuestions,
        isContentLoading,
        setIsContentLoading,
        isSuggestedBoxOpen,
        setIsSuggestedBoxOpen
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
